import { FC, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  AppSidebar,
  Background,
  Button,
  Checkbox,
  ErrorText,
  Input,
  Loading,
  LoadingOverlay,
} from '@/components';
import { ButtonColor, ButtonSize, Icons } from '@/enums';
import strings from '@/language/Swedish';
import useRegistrySearch, {
  RegistryProgressType,
  ResidenceType,
  TOO_MANY_COMBINATIONS,
} from '@/api/mutations/useRegistrySearch';
import useRegistryFields from '@/api/queries/useRegistryFields';
import {
  defaultFilterValues,
  registryColumns,
  registryFieldGroups,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
} from './registryConfig';
import MultiValueInput from './MultiValueInput';
import { counties, municipalities } from './svenskaRegioner';
import RegistryMap from './RegistryMap';
import ResidenceTable from './ResidenceTable';

const PageContainer = styled.div`
  ${tw`flex h-screen`};
`;

const Main = styled.main`
  ${tw`relative flex flex-col flex-1 gap-3 overflow-y-auto`};
  padding: 13px;
`;

const MapContainer = styled.div`
  ${tw`relative flex-1 min-h-[50%] w-full backdrop-blur-xl bg-transparent`};
`;

const GroupHeading = styled.span`
  ${tw`text-xs font-medium text-slate-800 mt-2 first:mt-0`};
`;

const FilterGroup = styled.div`
  ${tw`flex flex-col gap-1`};
`;

const FilterLabel = styled.label`
  ${tw`text-xs text-slate-600`};
`;

const SmallInput = styled(Input)`
  ${tw`h-8 text-xs`};
`;

const InfoMessage = styled.span`
  ${tw`text-xs text-slate-600`};
`;

const SidebarBottom = styled.div`
  ${tw`mt-auto pt-3`};
`;

const ProgressOverlay = styled.div`
  ${tw`absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-lg bg-white/80 pointer-events-none`};
`;

const ProgressTrack = styled.div`
  ${tw`h-2 w-64 rounded-full bg-slate-800/10 overflow-hidden`};
`;

const ProgressFill = styled.div<{ $percent: number }>`
  ${tw`h-full rounded-full bg-primary transition-all duration-300`};
  width: ${({ $percent }) => $percent}%;
`;

const ProgressText = styled.span`
  ${tw`text-[11px] text-slate-600 tabular-nums`};
`;

const regionSuggestions: Record<string, typeof counties | undefined> = {
  county_code: counties,
  municipality_code: municipalities,
};

const ResidentialRegistry: FC = () => {
  const [filters, setFilters] =
    useState<Record<string, string[]>>(defaultFilterValues);
  const [limit, setLimit] = useState('50');
  const [fetchAll, setFetchAll] = useState(false);
  const [residences, setResidences] = useState<ResidenceType[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [progress, setProgress] = useState<RegistryProgressType | null>(null);

  const {
    mutate: searchMutation,
    isPending,
    isError,
    error,
  } = useRegistrySearch();

  // Probe which fields this user's access level actually returns; filters and
  // columns adapt to it. Null/unknown falls back to showing everything.
  const { data: fieldKeys, isPending: fieldsPending } = useRegistryFields();
  const accessibleFields = fieldKeys ? new Set(fieldKeys) : null;

  const visibleGroups = registryFieldGroups
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (field) => !accessibleFields || accessibleFields.has(field.accessKey)
      ),
    }))
    .filter((group) => group.fields.length);

  const availableColumns = accessibleFields
    ? registryColumns.filter((column) => accessibleFields.has(column.name))
    : registryColumns;

  const handleFilterChange = (name: string, values: string[]) => {
    setFilters((previous) => ({ ...previous, [name]: values }));
  };

  const search = () => {
    if (isPending) return;

    if (fetchAll) setProgress({ fetched: 0, total: null });

    searchMutation(
      {
        filters,
        limit: parseInt(limit, 10) || 50,
        fetchAll,
        onProgress: setProgress,
      },
      {
        onSuccess: (result) => {
          setHasSearched(true);
          setResidences(result);
        },
        onSettled: () => setProgress(null),
      }
    );
  };

  const errorMessage =
    error instanceof Error && error.message === TOO_MANY_COMBINATIONS
      ? strings.registry.tooManyCombos
      : strings.registry.error;

  return (
    <PageContainer>
      <Background />
      <AppSidebar currentApp="registry">
        {fieldsPending && <Loading />}
        {!fieldsPending && visibleGroups.map((group) => (
          <FilterGroup key={group.title}>
            <GroupHeading>{group.title}</GroupHeading>
            {group.fields.map((field) => (
              <FilterGroup key={field.name}>
                <FilterLabel htmlFor={`registry-${field.name}`}>
                  {field.label}
                </FilterLabel>
                <MultiValueInput
                  id={`registry-${field.name}`}
                  placeholder={field.label}
                  values={filters[field.name] ?? []}
                  onChange={(values) => handleFilterChange(field.name, values)}
                  suggestions={regionSuggestions[field.name]}
                />
              </FilterGroup>
            ))}
          </FilterGroup>
        ))}
        <FilterGroup>
          <FilterLabel htmlFor="registry-limit">
            {strings.registry.limit}
          </FilterLabel>
          <SmallInput
            id="registry-limit"
            type={fetchAll ? 'text' : 'number'}
            min="1"
            disabled={fetchAll}
            value={fetchAll ? '–' : limit}
            onChange={(event) => setLimit(event.target.value)}
          />
          <Checkbox
            id="registry-fetch-all"
            label={strings.registry.fetchAll}
            tooltip={strings.registry.fetchAllInfo}
            isChecked={fetchAll}
            onToggle={() => setFetchAll((previous) => !previous)}
          />
        </FilterGroup>
        <Button
          color={ButtonColor.primary}
          size={ButtonSize.fullWidth}
          icon={Icons.search}
          isLoading={isPending}
          onClick={search}
        >
          {strings.registry.search}
        </Button>
        {isError && <ErrorText text={errorMessage} />}
        {hasSearched && !isError && !residences.length && (
          <InfoMessage>{strings.registry.noResults}</InfoMessage>
        )}
        <SidebarBottom>
          <Button
            color={ButtonColor.border}
            size={ButtonSize.fullWidth}
            icon={isMapVisible ? Icons.eyeClosed : Icons.eye}
            onClick={() => setIsMapVisible(!isMapVisible)}
          >
            {isMapVisible ? strings.registry.hideMap : strings.registry.showMap}
          </Button>
        </SidebarBottom>
      </AppSidebar>
      <Main>
        {isPending &&
          (progress ? (
            <ProgressOverlay>
              <Loading />
              <ProgressTrack>
                <ProgressFill
                  $percent={
                    progress.total
                      ? Math.min(
                          100,
                          (progress.fetched / progress.total) * 100
                        )
                      : 5
                  }
                />
              </ProgressTrack>
              <ProgressText>
                {progress.total
                  ? strings.registry.fetchProgress
                      .replace(
                        '@fetched',
                        progress.fetched.toLocaleString('sv-SE')
                      )
                      .replace(
                        '@total',
                        progress.total.toLocaleString('sv-SE')
                      )
                  : strings.registry.fetchProgressUnknown.replace(
                      '@fetched',
                      progress.fetched.toLocaleString('sv-SE')
                    )}
              </ProgressText>
            </ProgressOverlay>
          ) : (
            <LoadingOverlay />
          ))}
        {isMapVisible && (
          <MapContainer>
            <RegistryMap
              residences={residences}
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              availableColumns={availableColumns}
            />
          </MapContainer>
        )}
        <ResidenceTable
          residences={residences}
          availableColumns={availableColumns}
          expanded={!isMapVisible}
        />
      </Main>
    </PageContainer>
  );
};

export default ResidentialRegistry;
